import { useState, useCallback, useRef } from 'react';
import api from '../../../services/api';

export const useClients = (currentUser) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const abortControllerRef = useRef(null);
  const cacheRef = useRef(null);
  const cacheTimeRef = useRef(0);

  const fetchClients = useCallback(async (forceRefresh = false) => {
    // Cache de 30 secondes
    const CACHE_DURATION = 30000;
    const now = Date.now();
    
    if (!forceRefresh && cacheRef.current && (now - cacheTimeRef.current < CACHE_DURATION)) {
      setClients(cacheRef.current.clients);
      setCustomFields(cacheRef.current.customFields);
      setFilteredClients(cacheRef.current.filteredClients);
      setLoading(false);
      return;
    }

    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const { data } = await api.get('/opticians/clients', {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal
      });

      const sortedClients = data.clients.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setClients(sortedClients);
      setCustomFields(data.customFields);

      // Filtrer les clients avec actions selon le rôle
      const clientsWithActions = sortedClients.filter(c => 
        !['approved', 'rejected'].includes(c.status)
      );

      setFilteredClients(clientsWithActions);
      
      // Mettre en cache
      cacheRef.current = {
        clients: sortedClients,
        customFields: data.customFields,
        filteredClients: clientsWithActions
      };
      cacheTimeRef.current = now;
      
      setError('');
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setError('Erreur lors du chargement des clients');
        console.error(err);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, []);

  const updateClientStatus = useCallback(async (clientId, endpoint, data = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(endpoint.replace(':id', clientId), data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Vérifier si c'est un mode libre qui nécessite un paiement
      if (response.data?.requiresFreeAmountPayment) {
        // Ne pas recharger les clients, retourner les données pour le modal de paiement
        return {
          success: false,
          requiresFreeAmountPayment: true,
          ...response.data
        };
      }

      // Invalider le cache et recharger
      cacheRef.current = null;
      await fetchClients(true);
      return { success: true };
    } catch (err) {
      setError('Erreur lors de la mise à jour');
      console.error(err);
      return { success: false, error: err.response?.data?.message || 'Erreur lors de la mise à jour' };
    }
  }, [fetchClients]);

  const searchClients = useCallback((query) => {
    if (!query) {
      setFilteredClients(clients);
      return;
    }
    const filtered = clients.filter(client =>
      client.first_name.toLowerCase().includes(query.toLowerCase()) ||
      client.last_name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [clients]);

  return {
    clients,
    filteredClients,
    customFields,
    loading,
    error,
    fetchClients,
    updateClientStatus,
    searchClients,
    setFilteredClients
  };
};
